import { getUnifiedNavigation } from "@/navigation/unifiedNavigation";
import { NavigationItem } from "@/navigation/types";
import { AppStripId } from "@/components/AppStrip/appStripTypes";

const SECTION_TO_APP: Record<string, AppStripId> = {
  hub: "resources",
  stochastic: "stochastic",
  "event-registration": "events",
  "data-connector": "dashboards",
  admin: "admin",
  "company-admin": "admin",
  labs: "labs",
};

function flattenItems(items: NavigationItem[]): NavigationItem[] {
  const result: NavigationItem[] = [];
  for (const item of items) {
    result.push(item);
    if (item.children) {
      result.push(...flattenItems(item.children));
    }
  }
  return result;
}

function hrefMatchesPath(href: string, pathname: string): boolean {
  if (href === "#" || !href) return false;
  if (href.startsWith("http://") || href.startsWith("https://")) return false;
  if (href === "/hub") return pathname === "/hub";
  return pathname === href || pathname.startsWith(href + "/");
}

export function getAppStripIdFromPath(pathname: string): AppStripId | null {
  // The member homepage is its own app in the strip; every other /hub route
  // still belongs to Resources.
  if (pathname === "/hub" || pathname === "/hub/") return "home";
  // Coaching pages have no rail app of their own; they belong to Coaches
  // Learning inside the Learning Center.
  if (pathname === "/coaching" || pathname.startsWith("/coaching/"))
    return "learning-center";

  const sections = getUnifiedNavigation();

  let bestMatch: { sectionName: string; hrefLength: number } | null = null;

  for (const section of sections) {
    const sectionName = section.internalName;
    const children = section.children || [];
    const flatChildren = flattenItems(children);

    for (const item of flatChildren) {
      if (hrefMatchesPath(item.href, pathname)) {
        const len = item.href.length;
        if (!bestMatch || len > bestMatch.hrefLength) {
          bestMatch = { sectionName, hrefLength: len };
        }
      }
    }
  }

  if (!bestMatch) return null;
  return SECTION_TO_APP[bestMatch.sectionName] ?? null;
}

/**
 * Walk a section's nav tree and return the internalName keys of any
 * parent items whose children contain the active route. These are
 * the items that should be auto-expanded in the WideSidebar.
 */
export function getExpandedKeysForPath(pathname: string): string[] {
  const sections = getUnifiedNavigation();
  const keys: string[] = [];

  function walk(items: NavigationItem[]): boolean {
    for (const item of items) {
      const itemKey = item.internalName || item.name;
      if (item.children && item.children.length > 0) {
        const childMatches = walk(item.children);
        const selfMatches = hrefMatchesPath(item.href, pathname);
        if (childMatches || selfMatches) {
          keys.push(itemKey);
          return true;
        }
      } else if (hrefMatchesPath(item.href, pathname)) {
        return true;
      }
    }
    return false;
  }

  for (const section of sections) {
    walk(section.children || []);
  }

  return keys;
}
